/** Shared CRUD UI components for all feature views. */
import type { JSX } from 'preact'

// ── Styles ────────────────────────────────────────────────────────

const overlayStyle: Record<string, string | number> = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}

const modalCardStyle: Record<string, string | number> = {
  background: '#003344',
  border: '1px solid rgba(250,250,250,0.15)',
  borderRadius: 12,
  minWidth: 420,
  maxWidth: 560,
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

export const inputStyle: Record<string, string | number> = {
  width: '100%',
  padding: '8px 10px',
  background: 'rgba(0,34,51,0.8)',
  border: '1px solid rgba(250,250,250,0.15)',
  borderRadius: 6,
  color: '#FAFAFA',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
}

export const textareaStyle: Record<string, string | number> = {
  ...inputStyle,
  minHeight: 80,
  resize: 'vertical',
  fontFamily: 'inherit',
  lineHeight: 1.5,
}

export const selectStyle: Record<string, string | number> = {
  ...inputStyle,
  cursor: 'pointer',
}

const labelStyle: Record<string, string | number> = {
  fontSize: 12,
  color: 'rgba(250,250,250,0.5)',
  marginBottom: 4,
  display: 'block',
}

const actionBtnBase: Record<string, string | number> = {
  padding: '6px 16px',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
}

// ── ConfirmDialog ─────────────────────────────────────────────────

interface ConfirmDialogProps {
  message: string
  onConfirm: () => void
  onCancel: () => void
  confirmLabel?: string
  destructive?: boolean
}

export function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = 'Delete', destructive = true }: ConfirmDialogProps) {
  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={{ ...modalCardStyle, minWidth: 320, maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 14, color: 'rgba(250,250,250,0.85)', lineHeight: 1.5, marginBottom: 20 }}>{message}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onCancel} style={{ ...actionBtnBase, background: 'rgba(250,250,250,0.1)', color: 'rgba(250,250,250,0.7)' }}>
              Cancel
            </button>
            <button
              onClick={onConfirm}
              style={{ ...actionBtnBase, background: destructive ? '#c62828' : '#2DA5A0', color: '#fff' }}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── ModalDialog ───────────────────────────────────────────────────

interface ModalDialogProps {
  title: string
  onClose: () => void
  children: JSX.Element | JSX.Element[]
  footer?: JSX.Element
}

export function ModalDialog({ title, onClose, children, footer }: ModalDialogProps) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center' }}>
          <span style={{ flex: 1, fontWeight: 600, fontSize: 15 }}>{title}</span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'rgba(250,250,250,0.5)', cursor: 'pointer', fontSize: 16, padding: '2px 6px' }}
          >
            X
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>{children}</div>
        {footer && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(250,250,250,0.1)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

// ── FormField ─────────────────────────────────────────────────────

interface SelectOption {
  value: string | number
  label: string
}

interface FormFieldProps {
  label: string
  value: string
  onChange: (val: string) => void
  type?: 'text' | 'textarea' | 'select' | 'datetime-local' | 'date' | 'time' | 'number'
  options?: SelectOption[]
  placeholder?: string
  required?: boolean
}

export function FormField({ label, value, onChange, type = 'text', options, placeholder, required }: FormFieldProps) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: '#f44336' }}> *</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          style={textareaStyle}
          value={value}
          onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
          placeholder={placeholder}
        />
      ) : type === 'select' && options ? (
        <select style={selectStyle} value={value} onChange={(e) => onChange((e.target as HTMLSelectElement).value)}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          style={inputStyle}
          value={value}
          onInput={(e) => onChange((e.target as HTMLInputElement).value)}
          placeholder={placeholder}
        />
      )}
    </div>
  )
}

// ── Action Buttons ────────────────────────────────────────────────

interface SaveButtonProps {
  dirty: boolean
  saving: boolean
  onClick: () => void
}

export function SaveButton({ dirty, saving, onClick }: SaveButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={!dirty || saving}
      style={{
        ...actionBtnBase,
        background: dirty ? '#2DA5A0' : 'rgba(250,250,250,0.08)',
        color: dirty ? '#fff' : 'rgba(250,250,250,0.3)',
        opacity: saving ? 0.6 : 1,
        cursor: dirty && !saving ? 'pointer' : 'default',
      }}
    >
      {saving ? 'Saving...' : 'Save'}
    </button>
  )
}

interface DeleteButtonProps {
  onClick: () => void
}

export function DeleteButton({ onClick }: DeleteButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 10px',
        border: '1px solid rgba(244,67,54,0.4)',
        borderRadius: 6,
        background: 'transparent',
        color: '#f44336',
        cursor: 'pointer',
        fontSize: 12,
      }}
    >
      Delete
    </button>
  )
}

// ── Create Button (for view headers) ──────────────────────────────

interface CreateButtonProps {
  label: string
  onClick: () => void
}

export function CreateButton({ label, onClick }: CreateButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px',
        border: '1px solid rgba(45,165,160,0.4)',
        borderRadius: 6,
        background: 'rgba(45,165,160,0.15)',
        color: '#2DA5A0',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      + {label}
    </button>
  )
}

// ── Inline edit input (for detail panels) ─────────────────────────

interface InlineInputProps {
  value: string
  onChange: (val: string) => void
  type?: 'text' | 'textarea'
  placeholder?: string
  style?: Record<string, string | number>
}

export function InlineInput({ value, onChange, type = 'text', placeholder, style: extraStyle }: InlineInputProps) {
  const base = type === 'textarea' ? textareaStyle : inputStyle
  return type === 'textarea' ? (
    <textarea
      style={{ ...base, ...extraStyle }}
      value={value}
      onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
      placeholder={placeholder}
    />
  ) : (
    <input
      type="text"
      style={{ ...base, ...extraStyle }}
      value={value}
      onInput={(e) => onChange((e.target as HTMLInputElement).value)}
      placeholder={placeholder}
    />
  )
}
